<template>
  <div>
    <b-overlay
      :show="tariffsResponses('getTariffs') && allTariffsEmpty"
      spinner-type="border"
      variant="light"
      spinner-variant="primary"
      opacity="1"
      rounded>
      <div class="d-flex align-items-end pt-2 pb-6">
        <div class="d-flex flex-column">
          <h1 class="h5 mb-2">
            Tariffs
          </h1>
          <b-breadcrumb class="breadcrumb-transparent breadcrumb-line p-0 m-0">
            <b-breadcrumb-item :to="{ name: 'Dashboard' }">
              <i class="fa fa-home fa-sm mr-2"></i>
              Home
            </b-breadcrumb-item>
            <b-breadcrumb-item active>Settings</b-breadcrumb-item>
            <b-breadcrumb-item active>Tariffs</b-breadcrumb-item>
          </b-breadcrumb>
        </div>
        <div class="ml-auto">
          <b-button
            size="sm"
            v-wave
            variant="primary"
            class="align-middle btn-shadow"
            :disabled="tariffsResponses('getTariff') ||
              tariffsResponses('getTariffs') ||
              groupsResponses('groupsList') ||
              vpnServersResponses('vpnServersList')"
            @click="unselectTariff(), showModal('manageTariffModal')">
            <i class="fa fa-plus fa-sm p-0 pr-sm-2"></i>
            <span class="d-none d-sm-inline">Add New Tariff</span>
          </b-button>
        </div>
      </div>

      <b-card
        no-body
        class="card-custom card-border">

        <b-card-body>
          <div class="d-flex justify-content-between align-items-center mb-10">
            <b-form-checkbox
              id="showArchieved"
              v-model="showArchieved"
              name="showArchieved"
              class="font-weight-500">
              <span class="font-weight-bold">
                Show archieved
              </span>
            </b-form-checkbox>
          </div>
          <b-table
            :items="allTariffs"
            :fields="visibleFields"
            :class="{ 'table-hover' : !allTariffsEmpty }"
            :tbody-tr-class="rowClass"
            head-variant=""
            stacked="sm"
            show-empty
            responsive
            class="">
            <template #cell(order)="data">
              <span
                v-if="data.item.type === 'active'"
                class="small font-weight-light">
                {{ data.index + 1 }}
              </span>
              <span
                v-else
                class="small font-weight-light opacity-30">&bull;</span>
            </template>
            <template #cell(name)="data">
              <span
                class="d-inline-block text-truncate line-height-xl"
                style="max-width: 250px;"
                :class="{ 'opacity-70' : tariffIsArchieved(data.item._id) }">{{ data.item.name }}</span>
            </template>
            <template #cell(code)="data">
              <span :class="{ 'opacity-70' : tariffIsArchieved(data.item._id) }">
                {{ data.item.code }}
              </span>
            </template>
            <template #cell(active)="data">
              <div
                v-if="data.item.type === 'active'"
                class="d-flex justify-center-start justify-content-md-center">
                <!-- Wrapper for fake element + checkbox -->
                <div
                  class="position-relative"
                  style="width: fit-content;">
                  <!-- Fake element above checkbox that TRIGGERS event -->
                  <div
                    class="w-100 h-100 position-absolute"
                    style="z-index: 20;"
                    role="button"
                    @click="handleStatusChange(data.item)" />
                  <!-- Checkbox that REPRESENTS current status, not changes it -->
                  <b-form-checkbox
                    :checked="data.item.active === 'on'"
                    size="lg"
                    switch
                    name="switch-vpn-server"
                    class="switch-success ml-0 ml-md-3 mb-1 mb-md-2" />
                </div>
              </div>
              <b-form-checkbox
                v-else
                :checked="false"
                disabled
                size="lg"
                switch
                class="switch-success ml-0 ml-md-3 mb-1 mb-md-2 opacity-75" />
            </template>
            <template #cell(devices)="data">
              <span
                v-if="data.item.devices"
                :class="{ 'opacity-70' : tariffIsArchieved(data.item._id) }">
                {{ data.item.devices }}
              </span>
              <i
                v-else
                class="fa fa-minus fa-xs opacity-50" />
            </template>
            <template #cell(users)="data">
              <span
                v-if="data.item.users"
                :class="{ 'opacity-70' : tariffIsArchieved(data.item._id) }">
                {{ data.item.users }}
              </span>
              <i
                v-else
                class="fa fa-minus fa-xs opacity-50" />
            </template>
            <template #cell(total_referrals)="data">
              <span
                v-if="data.item.total_referrals"
                :class="{ 'opacity-70' : tariffIsArchieved(data.item._id) }">
                {{ data.item.total_referrals }}
              </span>
              <i
                v-else
                class="fa fa-minus fa-xs opacity-50" />
            </template>
            <template #cell(group)="data">
              <span v-if="!tariffGroupEmpty(data.item._id)">
                <i class="fas fa-folder-open fa-sm"></i>
                <span :class="{ 'opacity-70' : tariffIsArchieved(data.item._id) }">
                  {{ data.item.group.name }}
                </span>
              </span>
              <i
                v-else
                class="fa fa-minus fa-xs opacity-50" />
            </template>
            <template #cell(vpn_servers)="data">
              <div class="d-flex">
                <div v-if="!tariffVpnServersEmpty(data.item._id)">
                  <span
                    v-for="vpnServer in tariffVpnServers(data.item._id)"
                    :key="vpnServer._id"
                    v-b-tooltip.hover
                    :title="vpnServer.name"
                    class="svg-icon svg-icon svg-icon-sm mr-3">
                    <inline-svg
                      :src="`media/svg/icons/VPN/${vpnServer.code}.svg`"
                      class="svg-icon m-0"
                      :class="{ 'opacity-70' : tariffIsArchieved(data.item._id) }" />
                  </span>
                </div>
                <i
                  v-else
                  class="fa fa-minus fa-xs opacity-50" />
              </div>
            </template>
            <template #cell(edit)="data">
              <b-dropdown
                v-if="data.item.type === 'active'"
                no-caret
                :boundary="allTariffsLength < 5 ? 'window' : 'scrollParent'"
                right
                variant="outline-primary"
                toggle-class="btn-icon"
                size="sm"
                class="dropdown-inline">
                <template v-slot:button-content>
                  <i class="fa fa-ellipsis-h fa-sm"></i>
                </template>
                <b-dropdown-item-button
                  :disabled="tariffsResponses('getTariff') || tariffsResponses('getTariffs')"
                  @click="showModal('manageTariffModal', data.item)">
                  <i class="fa fa-pen fa-sm mr-1"></i>
                  Edit tariff
                </b-dropdown-item-button>
                <b-dropdown-item-button
                  :disabled="tariffsResponses('getTariff') || tariffsResponses('getTariffs') || tariffResellersEmpty(data.item._id)"
                  @click="showModal('migrateResellersModal', data.item)">
                  <i class="fa fa-people-arrows fa-sm mr-1"></i>
                  Migrate resellers
                </b-dropdown-item-button>
                <b-dropdown-divider />
                <b-dropdown-item-button
                  :disabled="tariffsResponses('archiveTariff')"
                  @click="archiveTariff(data.item._id)">
                  <i class="fa fa-archive fa-sm mr-1"></i>
                  Archive tariff
                </b-dropdown-item-button>
              </b-dropdown>
              <b-button
                v-else
                size="sm"
                variant="link-primary"
                :disabled="tariffsResponses('restoreTariff')"
                @click="restoreTariff(data.item._id)">
                <i class="fa fa-trash-restore fa-sm"></i>
                <span>Restore</span>
              </b-button>
            </template>
          </b-table>

          <div class="w-100 d-flex align-items-center flex-wrap pt-6">
            <b-dropdown
              variant="hover-light-primary"
              id="custiomize-table"
              no-caret
              right
              size="sm"
              class="">
              <template #button-content>
                <span>Customize Table</span>
                <i class="fa fa-cog fa-sm pl-2 pr-0"></i>
              </template>
              <div class="d-flex flex-column px-3">
                <b-form-checkbox
                  v-for="(field, index) in fields"
                  :key="`dropdown-item-${field.key}`"
                  v-model="field.visible"
                  v-show="index > 0"
                  :disabled="field.default"
                  size="md"
                  class="my-1">
                  {{ field.label }}
                </b-form-checkbox>
              </div>
            </b-dropdown>
          </div>
        </b-card-body>
      </b-card>
    </b-overlay>

    <modal-disable-tariff />
    <modal-manage-tariff />
    <modal-migrate-resellers />
    
  </div>
</template>

<script>
import {
  // actions
  GET_TARIFFS,
  GET_TARIFF,
  SWITCH_TARIFF,
  ARCHIVE_TARIFF,
  RESTORE_TARIFF,

  // mutations
  MARK_FOR_SWITCH_TARIFF,
  SELECT_TARIFF,
  UNSELECT_TARIFF,
} from '@/core/services/store/tariffs.module'
import { GET_GROUPS } from '@/core/services/store/groups.module'
import { GET_VPN_SERVERS } from '@/core/services/store/vpn-servers.module'
import { mapGetters } from 'vuex'
import { eventBus } from '@/main'
import {
  showToast,
  hideModal,
  showDeletePopover,
  hideDeletePopover,
  restoreTableColumns
} from '@/core/utils'
import ModalDisableTariff from './components/ModalDisableTariff.vue'
import ModalManageTariff from './components/ModalManageTariff.vue'
import ModalMigrateResellers from './components/ModalMigrateResellers.vue'

export default {
  name: 'SettingsTariffs',
  components: {
    ModalDisableTariff,
    ModalManageTariff,
    ModalMigrateResellers,
  },
  data() {
    return {
      fields: [
        {
          label: '#',
          key: 'order',
          class: ['align-middle', 'pl-5'],
          thStyle: 'width: 50px;',
          visible: true,
          default: true,
        },
        {
          label: 'Name',
          key: 'name',
          class: ['align-middle', 'font-weight-bold'],
          visible: true,
          default: true,
        },
        {
          label: 'Code',
          key: 'code',
          class: ['align-middle', 'text-nowrap'],
          visible: true,
          default: false,
        },
        {
          label: 'Status',
          key: 'active',
          class: ['align-middle', 'text-nowrap', 'text-center'],
          visible: true,
          default: false,
        },
        {
          label: 'Devices',
          key: 'devices',
          class: ['align-middle', 'text-left text-md-right'],
          visible: true,
          default: false,
        },
        {
          label: 'Users',
          key: 'users',
          class: ['align-middle', 'text-left text-md-right'],
          visible: true,
          default: false,
        },
        {
          label: 'Referrals',
          key: 'total_referrals',
          class: ['align-middle', 'text-left text-md-right'],
          visible: true,
          default: false,
        },
        {
          label: 'Group',
          key: 'group',
          class: ['align-middle', 'text-nowrap', 'font-size-sm', 'pl-15'],
          visible: true,
          default: false,
        },
        {
          label: 'VPN Servers',
          key: 'vpn_servers',
          class: ['align-middle', 'text-nowrap'],
          visible: true,
          default: false,
        },
        {
          label: 'Actions',
          key: 'edit',
          class: ['align-middle', 'text-md-right', 'text-nowrap', 'pr-5', 'pl-10'],
          thStyle: 'width: 150px;',
          visible: true,
          default: true,
        },
      ],

      showArchieved: false,

      showToast,
      hideModal,
      showDeletePopover,
      hideDeletePopover,
      restoreTableColumns,
    }
  },
  computed: {
    ...mapGetters([
      'activeTariffs',
      'activeTariffsLength',
      'archievedTariffs',
      'archievedTariffsLength',
      'allTariffs',
      'allTariffsLength',
      'allTariffsEmpty',
      'tariffVpnServers',
      'tariffVpnServersEmpty',
      'tariffIsArchieved',
      'tariffGroupEmpty',
      'markedForSwitchTariff',
      'tariffResellersEmpty',
      'tariffsResponses',
      'groupsResponses',
      'vpnServersResponses',
    ]),

    visibleFields() {
      return this.fields.filter(field => field.visible)
    }
  },
  watch: {
    visibleFields() {
      localStorage.setItem('tariffsTableColumns', JSON.stringify(this.fields))
    },
  },
  created() {
    eventBus.$on('showToast/SettingsTariffs', ({ message, variant }) => {
      this.showToast(message, variant)
    })

    this.restoreTableColumns('tariffsTableColumns')
    this.getTariffs('active')
    this.getTariffs('archieved')
  },
  destroyed() {
    eventBus.$off('showToast/SettingsTariffs')
  },
  methods: {
    dataIsReady(data) {
      let result = []
      for (let i = 0; i < data.length; i++) {
        result.push(Array.isArray(data[i]) || typeof data == 'object')
      }
      return result.every(el => el)
    },

    showModal(type, tariff) {
      let getTariff = () => {
        if (!tariff) {
          return true
        }
        return this.$store
          .dispatch(GET_TARIFF, {
            id: tariff._id,
            type: tariff.type
          })
          .then(data => {
            this.selectTariff(data)
          })
      }

      let requirements = {
        manageTariffModal: [
          getTariff,
          () => this.$store.dispatch(GET_GROUPS),
          () => this.$store.dispatch(GET_VPN_SERVERS, {})
        ],
        migrateResellersModal: [
          getTariff
        ],
      }

      if (requirements.hasOwnProperty(type)) {
        Promise
          .all(requirements[type].map(promiseFn => promiseFn()))
          .then(res => {
            if (this.dataIsReady(res)) {
              eventBus.$emit('openModal', type)
            }
          })
      } else {
        eventBus.$emit('openModal', type)
      }
    },

    rowClass(item, type) {
      if (item && type === 'row') {
        if (item.type === 'archieved') {
          if (this.showArchieved) {
            return 'bg-gray-100 d-table-row'
          } else {
            return 'd-none'
          }
        }
      } else {
        return null
      }
    },

    getTariffs(type) {
      this.$store.dispatch(GET_TARIFFS, { type })
    },

    markForSwitch(tariff) {
      this.$store.commit(MARK_FOR_SWITCH_TARIFF, tariff)
    },

    handleStatusChange(tariff) {
      this.markForSwitch(tariff)

      if (tariff.active === 'on') {
        this.showModal('confirmModal')
      } else {
        this.switchTariff()
      }
    },

    switchTariff() {
      this.$store.dispatch(SWITCH_TARIFF)
    },

    selectTariff(tariff) {
      this.$store.commit(SELECT_TARIFF, tariff)
    },

    unselectTariff() {
      this.$store.commit(UNSELECT_TARIFF)
    },

    archiveTariff(id) {
      this.$store
        .dispatch(ARCHIVE_TARIFF, { id })
        .then(() => {
          this.getTariffs('active')
          this.getTariffs('archieved')
        })
    },

    restoreTariff(id) {
      this.$store
        .dispatch(RESTORE_TARIFF, { id })
        .then(() => {
          this.getTariffs('active')
          this.getTariffs('archieved')
        })
    },
  }
}
</script>
